import { useTranslation } from 'react-i18next'

const DOMAIN_MIN = 55
const DOMAIN_MAX = 145
const MEAN = 100
const STANDARD_DEVIATION = 15
const WIDTH = 360
const HEIGHT = 250
const PLOT_LEFT = 24
const PLOT_RIGHT = 336
const PLOT_TOP = 24
const BASELINE_Y = 175
const TICK_Y = 198
const BADGE_Y = 226
const BADGE_HEIGHT = 28
const BADGE_TOP = BADGE_Y - BADGE_HEIGHT / 2
const TICKS = [70, 85, 100, 115, 130] as const

function normalPdf(value: number) {
  const z = (value - MEAN) / STANDARD_DEVIATION
  return Math.exp(-0.5 * z * z) / (STANDARD_DEVIATION * Math.sqrt(2 * Math.PI))
}

function xForScore(score: number) {
  const clamped = Math.min(Math.max(score, DOMAIN_MIN), DOMAIN_MAX)
  return PLOT_LEFT + ((clamped - DOMAIN_MIN) / (DOMAIN_MAX - DOMAIN_MIN)) * (PLOT_RIGHT - PLOT_LEFT)
}

const PEAK = normalPdf(MEAN)

function yForScore(score: number) {
  const density = normalPdf(Math.min(Math.max(score, DOMAIN_MIN), DOMAIN_MAX))
  return BASELINE_Y - (density / PEAK) * (BASELINE_Y - PLOT_TOP)
}

const CURVE_POINTS = Array.from({ length: DOMAIN_MAX - DOMAIN_MIN + 1 }, (_, index) => {
  const score = DOMAIN_MIN + index
  return { x: xForScore(score), y: yForScore(score) }
})

const CURVE_PATH = CURVE_POINTS.map(({ x, y }, index) =>
  `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`,
).join(' ')
const AREA_PATH = `${CURVE_PATH} L${PLOT_RIGHT} ${BASELINE_Y} L${PLOT_LEFT} ${BASELINE_Y} Z`

const COMPACT_WIDTH = 242
const COMPACT_HEIGHT = 112
const COMPACT_PLOT_LEFT = 5
const COMPACT_PLOT_RIGHT = 237
const COMPACT_PLOT_TOP = 1
const COMPACT_BASELINE_Y = 73
const COMPACT_TICK_Y = 80
const COMPACT_BADGE_TOP = 88
const COMPACT_BADGE_HEIGHT = 21

function compactXForScore(score: number) {
  const clamped = Math.min(Math.max(score, DOMAIN_MIN), DOMAIN_MAX)
  return COMPACT_PLOT_LEFT + ((clamped - DOMAIN_MIN) / (DOMAIN_MAX - DOMAIN_MIN)) * (COMPACT_PLOT_RIGHT - COMPACT_PLOT_LEFT)
}

function compactYForScore(score: number) {
  const density = normalPdf(Math.min(Math.max(score, DOMAIN_MIN), DOMAIN_MAX))
  return COMPACT_BASELINE_Y - (density / PEAK) * (COMPACT_BASELINE_Y - COMPACT_PLOT_TOP)
}

const COMPACT_CURVE_PATH = Array.from({ length: DOMAIN_MAX - DOMAIN_MIN + 1 }, (_, index) => {
  const score = DOMAIN_MIN + index
  const x = compactXForScore(score)
  const y = compactYForScore(score)
  return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`
}).join(' ')
const COMPACT_AREA_PATH = `${COMPACT_CURVE_PATH} L${COMPACT_PLOT_RIGHT} ${COMPACT_BASELINE_Y} L${COMPACT_PLOT_LEFT} ${COMPACT_BASELINE_Y} Z`

function CompactAiqBellCurve({ score, className }: { score: number; className: string }) {
  const { t } = useTranslation('admin')
  const x = Math.round(compactXForScore(score) * 100) / 100
  const scoreY = Math.round(compactYForScore(score) * 100) / 100
  const meanX = compactXForScore(MEAN)

  return (
    <svg
      viewBox={`0 0 ${COMPACT_WIDTH} ${COMPACT_HEIGHT}`}
      role="img"
      aria-label={t('diagnosticReport.aiqCurve.ariaLabel', { score })}
      className={`h-[112px] w-[242px] overflow-visible ${className}`}
      preserveAspectRatio="xMidYMid meet"
      data-variant="compact"
    >
      <path d={COMPACT_AREA_PATH} fill="#4599ff" opacity="0.1" />
      <path d={COMPACT_CURVE_PATH} fill="none" stroke="#4599ff" strokeWidth="1.5" strokeLinejoin="round" />
      <line
        x1={meanX}
        x2={meanX}
        y1={COMPACT_PLOT_TOP}
        y2={COMPACT_BASELINE_Y}
        stroke="#b8cfff"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <line
        data-testid="aiq-position-line"
        x1={x}
        x2={x}
        y1={scoreY}
        y2={COMPACT_BASELINE_Y}
        stroke="#4599ff"
        strokeWidth="1.5"
        strokeDasharray="2 2"
      />
      <text x={meanX} y="65" textAnchor="middle" fontSize="8" fontWeight="500" fill="#888">
        {t('diagnosticReport.aiqCurve.average')}
      </text>
      {TICKS.map((tick) => (
        <text key={tick} x={compactXForScore(tick)} y={COMPACT_TICK_Y} textAnchor="middle" fontSize="8" fontWeight="500" fill="#888">
          {tick}
        </text>
      ))}
      <polygon
        data-testid="aiq-position-marker"
        data-x={x}
        data-badge-top={COMPACT_BADGE_TOP}
        points={`${x},${COMPACT_BADGE_TOP - 5} ${x - 5},${COMPACT_BADGE_TOP} ${x + 5},${COMPACT_BADGE_TOP}`}
        fill="#4599ff"
      />
      <g data-testid="aiq-score-badge" data-y={COMPACT_BADGE_TOP + COMPACT_BADGE_HEIGHT / 2} data-top={COMPACT_BADGE_TOP}>
        <rect x={x - 18} y={COMPACT_BADGE_TOP} width="36" height={COMPACT_BADGE_HEIGHT} rx="10.5" fill="#4599ff" />
        <text
          data-testid="aiq-marker"
          x={x}
          y={COMPACT_BADGE_TOP + COMPACT_BADGE_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fontWeight="600"
          fill="white"
        >
          {score}
        </text>
      </g>
    </svg>
  )
}

export function AiqBellCurve({
  score,
  className = '',
  variant = 'default',
}: {
  score: number
  className?: string
  variant?: 'default' | 'compact'
}) {
  const { t } = useTranslation('admin')

  if (variant === 'compact') {
    return <CompactAiqBellCurve score={score} className={className} />
  }

  const x = Math.round(xForScore(score) * 100) / 100
  const curveY = Math.round(yForScore(score) * 100) / 100

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={t('diagnosticReport.aiqCurve.ariaLabel', { score })}
      className={`h-auto w-full max-w-[360px] overflow-visible ${className}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {TICKS.map((tick) => {
        const tickX = xForScore(tick)
        return (
          <line
            key={`grid-${tick}`}
            x1={tickX}
            x2={tickX}
            y1={PLOT_TOP}
            y2={BASELINE_Y}
            stroke="#d8dce5"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        )
      })}
      <path d={AREA_PATH} fill="#5E86FF" opacity="0.1" />
      <path d={CURVE_PATH} fill="none" stroke="#5E86FF" strokeWidth="2" strokeLinejoin="round" />
      <line
        data-testid="aiq-position-line"
        x1={x}
        x2={x}
        y1={curveY}
        y2={BASELINE_Y}
        stroke="#5E86FF"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <polygon
        data-testid="aiq-position-marker"
        data-x={x}
        data-badge-top={BADGE_TOP}
        points={`${x},${BADGE_TOP - 10} ${x - 7},${BADGE_TOP} ${x + 7},${BADGE_TOP}`}
        fill="#5E86FF"
      />
      <text x={xForScore(100)} y="164" textAnchor="middle" fontSize="12" fontWeight="500" fill="#7E7F83">
        {t('diagnosticReport.aiqCurve.average')}
      </text>
      {TICKS.map((tick) => (
        <text key={tick} x={xForScore(tick)} y={TICK_Y} textAnchor="middle" fontSize="12" fontWeight="500" fill="#7E7F83">
          {tick}
        </text>
      ))}
      <g data-testid="aiq-score-badge" data-y={BADGE_Y} data-top={BADGE_TOP}>
        <rect x={x - 23} y={BADGE_TOP} width="46" height={BADGE_HEIGHT} rx="14" fill="#5E86FF" />
        <text data-testid="aiq-marker" x={x} y={BADGE_Y} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="700" fill="white">
          {score}
        </text>
      </g>
    </svg>
  )
}
